import { useMemo } from 'react';
import { Airline4XRules } from '../constants/server-constants';
/**
 * Segments Domain
 * Types, store, handlers, and service methods for flight segment management.
 * Mirrors the server's SegmentStore structure for consistency.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { aircraftOf, getCurrentFleet, getFleetSlot, handleFleetUpdate } from './fleets';
import { NOT_SENT_WORDS, sendGatedCommand, type NotSentReason } from './command-gate';
import type { Fleet } from './fleets';
import {
  handleAirportUpdate,
  useAirportsStore,
  type Airport,
  cityInfluence,
  CITY_BASE_INFLUENCE,
} from './airports';
import { useTeamsStore } from './teams';
import { getViewerUserId } from './current-team';
import { OpCode, MessageType } from '../constants/op-codes';
import { showErrorToast } from './toasts';
import i18n from '../i18n/config';
import { routeCodes } from '../data/cities';
import {
  airportTooSmall,
  allFlying,
  investToFly,
  kmShort,
  mostADay,
  noSlotNote,
  outsideNetworkNote,
  rangeShort,
  smallerEndOf,
} from './new-flight-words';
import { refusalKey, refusalText, type OrderBlock, type OrderRefusal } from './order-block';
import { sendToMatch } from '../services/nakama/match-send';

// ==================== TYPES ====================

/**
 * Segment represents a flight route segment operated by a team.
 * Mirrors the server's Segment struct in segments.go.
 */
export interface FlightInstance {
  /** Unique identifier for this flight instance */
  flight_id: number;
  /** Flight number for this instance */
  flight_number: string;
  /** Flights per day for this instance */
  flights_per_day: number;
  /** User ID of the player who created this flight instance (optional) */
  created_by_user_id?: string;
  /** Display name of the player who created this flight instance (optional) */
  created_by_name?: string;
  /** Quarter when this flight instance was created (optional, 1-based) */
  created_at_quarter?: number;
  /** One of the aircraft past your share of the origin's efficient positions at the last update: its costs there carry the surcharge (overflowEnds). */
  overflow_origin?: boolean;
  /** The same at the destination. */
  overflow_destination?: boolean;
  congestion_cost?: number;
}

/**
 * Segment represents a flight route segment operated by a team.
 * Mirrors the server's Segment struct in segments.go.
 */
export interface Segment {
  /** Unique identifier for this segment */
  segment_id: number;
  /** Team ID that owns this segment */
  team_id: number;
  /** Origin airport ID (always ordered lowest to highest with dest) */
  airport_id_origin: number;
  /** Destination airport ID (always ordered lowest to highest with origin) */
  airport_id_dest: number;
  /** Index into the team's flat fleet slots array */
  fleet_slot_index: number;
  /** Number of planes assigned to this segment (max 10) */
  plane_count: number;
  /** Distance in kilometers between origin and destination */
  distance: number;
  /** Flight time in minutes */
  flight_time: number;
  /** Maximum possible flights per day based on round trip time */
  max_flights_per_day: number;
  /** Current flights per day (can be adjusted, starts at min 1) */
  current_flights_per_day: number;
  /** Number of seats per plane for each class [ECON, BUSINESS, FIRST] */
  seats_per_plane: [number, number, number];
  /** Total seats per day (SeatsPerPlane * PlaneCount * CurrentFlightsPerDay) */
  seats_per_day: [number, number, number];
  /** Number of seats sold per class */
  seats_sold: [number, number, number];
  /** Comfort rating for this segment [ECON, BUSINESS, FIRST] */
  comfort_score: [number, number, number];
  /** Cost per seat per class [ECON, BUSINESS, FIRST] */
  cost_per_seat: [number, number, number];
  /** Per-passenger service cost per class [ECON, BUSINESS, FIRST] */
  per_passenger_service: [number, number, number];
  /** Total fuel cost for round trip */
  total_fuel_cost: number;
  /** Total landing fees for round trip (both airports) */
  total_landing_fees: number;
  /** Total crew cost for round trip (hourly opex) */
  total_crew_cost: number;
  /** Total ground handling cost for round trip */
  total_ground_cost: number;
  /** Average seats filled per day */
  avg_seats_day: [number, number, number];
  /** Percentage of seats filled per day (0-100) */
  load_factor: [number, number, number];
  /** Exact number of seats sold in the period */
  seats_sold_period: [number, number, number];
  /** Number of itineraries that use this segment (direct + connecting) */
  itinerary_count: number;
  /** Attributed daily revenue from all itineraries using this segment */
  daily_revenue: number;
  /** Attributed daily operating cost for this segment */
  daily_cost: number;
  /** Daily fuel cost (round-trip fuel × flights/day) */
  daily_fuel_cost: number;
  /** Daily crew cost (round-trip crew × flights/day) */
  daily_crew_cost: number;
  /** Daily landing fees (round-trip landing × flights/day) */
  daily_landing_fees: number;
  /** Daily ground handling cost (round-trip ground × flights/day) */
  daily_ground_cost: number;
  /** Daily per-passenger service cost */
  daily_service_cost: number;
  daily_congestion_cost?: number;
  congestion_cost?: number;
  /** Per-plane flight instances on this segment */
  flights: FlightInstance[];
}

/**
 * SegmentStoreData mirrors the server's SegmentStore structure.
 * Contains both the segments map and the city pair index for fast lookups.
 */
export interface SegmentStoreData {
  /** Map of segment_id -> Segment */
  segments: Record<number, Segment>;
  /** Map of cityPairKey -> segment_ids for fast route lookups */
  byCityPair: Record<number, number[]>;
}

function makeTeamRouteKey(teamId: number, airportIdOrigin: number, airportIdDest: number): string {
  return `${teamId}-${airportIdOrigin}-${airportIdDest}`;
}

function buildByTeamRouteIndex(segments: Record<number, Segment>): Record<string, number[]> {
  const byTeamRoute: Record<string, number[]> = {};
  for (const segment of Object.values(segments)) {
    const routeKey = makeTeamRouteKey(segment.team_id, segment.airport_id_origin, segment.airport_id_dest);
    if (!byTeamRoute[routeKey]) {
      byTeamRoute[routeKey] = [];
    }
    byTeamRoute[routeKey].push(segment.segment_id);
  }
  return byTeamRoute;
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Creates a city pair key from two airport IDs.
 * Always orders with lower ID first for consistent lookups (same logic as server).
 */
export function makeCityPairKey(airportId1: number, airportId2: number): number {
  const lower = Math.min(airportId1, airportId2);
  const higher = Math.max(airportId1, airportId2);
  return (lower << 16) | higher;
}

/**
 * Orders airport IDs to ensure origin < dest (same logic as server).
 */
function orderAirportIds(airportId1: number, airportId2: number): [number, number] {
  if (airportId1 < airportId2) {
    return [airportId1, airportId2];
  }
  return [airportId2, airportId1];
}

/**
 * Every segment flown between two airports, by any team, read through the
 * city-pair index. A pure function over the store data, so a test can reach it
 * without standing up a store: `byCityPair` is keyed by an ordered pair, so a
 * route reads the same from either end, and an id the index still lists after
 * its segment went is dropped rather than handed on as undefined.
 */
export function selectSegmentsByCityPair(
  store: SegmentStoreData,
  airportId1: number,
  airportId2: number,
): Segment[] {
  const ids = store.byCityPair[makeCityPairKey(airportId1, airportId2)] || [];
  return ids.map((id) => store.segments[id]).filter(Boolean);
}

// ==================== OVERFLOW ====================

/**
 * The airports an aircraft overflows at, as the last update settled it: its
 * route's origin, its destination, or both — where its crew and ground costs
 * carry the airport's surcharge (congestion.go, ResolveAirportCongestion).
 */
export function overflowEnds(segment: Pick<Segment, 'airport_id_origin' | 'airport_id_dest'>, flight: Pick<FlightInstance, 'overflow_origin' | 'overflow_destination'>): number[] {
  const ends: number[] = [];
  if (flight.overflow_origin) ends.push(segment.airport_id_origin);
  if (flight.overflow_destination) ends.push(segment.airport_id_dest);
  return ends;
}

// ==================== A SEGMENT'S DAY ====================

/**
 * Whether the accountant has reached a segment yet, setting its revenue and
 * cost a day (accountant.go). Its cost stays 0 until the first update after it
 * is added — which is how the flight panel knows to say n/a, and Team HQ that
 * a route's figures are still to come.
 */
export function hasDailyFigures(segment: Segment): boolean {
  return segment.daily_cost > 0;
}

/** A team's segments: each sub-fleet's route, flying or not. None for no team. */
function teamSegments(segments: Record<number, Segment>, teamId: number | undefined): Segment[] {
  if (teamId === undefined) return [];
  return Object.values(segments).filter((segment) => segment.team_id === teamId);
}

/** Reactive teamSegments, kept while the segments and the team hold still. */
export function useTeamSegments(teamId: number | undefined): Segment[] {
  const segments = useSegmentsStore((s) => s.store.segments);
  return useMemo(() => teamSegments(segments, teamId), [segments, teamId]);
}

/** The cities a team's routes touch: every origin and destination of its segments. */
export function citiesServedBy(segments: Record<number, Segment>, teamId: number): Set<number> {
  const served = new Set<number>();
  for (const segment of Object.values(segments)) {
    if (segment.team_id !== teamId) continue;
    served.add(segment.airport_id_origin);
    served.add(segment.airport_id_dest);
  }
  return served;
}

/**
 * The game's load-factor bands, in percent: under `low` a flight flies too
 * empty, from `high` it flies full — the target the Flights chart draws.
 */
export const LOAD_FACTOR_BANDS = { low: 40, high: 70 } as const;

/** How full a flight flies, judged by the game's bands. */
export type LoadFactorBand = 'poor' | 'fair' | 'good';

/**
 * A load factor's band, 0–100: poor under the low band, good from the high
 * one, fair between. The globe's load-factor arcs, a flight's cabin pills and
 * Team HQ's routes all judge by it.
 */
export function loadFactorBand(loadFactor: number): LoadFactorBand {
  if (loadFactor < LOAD_FACTOR_BANDS.low) return 'poor';
  return loadFactor >= LOAD_FACTOR_BANDS.high ? 'good' : 'fair';
}

/**
 * How full these segments fly, 0–100: each class's load factor weighted by
 * the seats it offers a day, so a route's big cabin counts for more than its
 * small one. Read `load_factor`, which the server sets at every update —
 * never `seats_sold`, which it zeroes as each update ends (FinalizeTick,
 * sim_adapter.go). 0 when the segments offer no seats.
 */
export function seatWeightedLoadFactor(segments: readonly Segment[]): number {
  let seats = 0;
  let filled = 0;
  for (const segment of segments) {
    for (let cabin = 0; cabin < 3; cabin++) {
      seats += segment.seats_per_day[cabin];
      filled += segment.seats_per_day[cabin] * segment.load_factor[cabin];
    }
  }
  return seats > 0 ? filled / seats : 0;
}

// ==================== HQ REACH VALIDATION ====================

/** Where a team's network starts: its headquarters (`hqId`, null before it has one) and every Base of its. */
function networkRoots(airports: Record<number, Airport>, teamId: number, hqId: number | null): Set<number> {
  const roots = new Set<number>();
  if (hqId !== null) roots.add(hqId);
  for (const airport of Object.values(airports)) {
    if (cityInfluence(airport, teamId) >= CITY_BASE_INFLUENCE) roots.add(airport.id);
  }
  return roots;
}

function getHQRootAirportIds(airports: Record<number, Airport>, teamId: number): Set<number> {
  const team = Object.values(useTeamsStore.getState().teams).find(team => team.team_id === teamId);
  return networkRoots(airports, teamId, team?.hq_established ? team.hq : null);
}

function buildTeamAirportAdjacency(teamId: number, segments: Record<number, Segment>): Map<number, Set<number>> {
  const adjacency = new Map<number, Set<number>>();
  for (const segment of Object.values(segments)) {
    if (!segment || segment.team_id !== teamId || segment.plane_count <= 0) continue;
    const origin = segment.airport_id_origin;
    const dest = segment.airport_id_dest;
    if (!adjacency.has(origin)) adjacency.set(origin, new Set<number>());
    if (!adjacency.has(dest)) adjacency.set(dest, new Set<number>());
    adjacency.get(origin)!.add(dest);
    adjacency.get(dest)!.add(origin);
  }
  return adjacency;
}

function computeReachableAirportsFromHQ(
  roots: Set<number>,
  adjacency: Map<number, Set<number>>,
  maxHops: number
): Set<number> {
  const reachable = new Set<number>();
  const queue: Array<{ airportId: number; hops: number }> = [];

  roots.forEach((root) => {
    reachable.add(root);
    queue.push({ airportId: root, hops: 0 });
  });

  for (let i = 0; i < queue.length; i++) {
    const { airportId, hops } = queue[i];
    if (hops >= maxHops) continue;
    const neighbors = adjacency.get(airportId);
    if (!neighbors) continue;
    neighbors.forEach((neighbor) => {
      if (reachable.has(neighbor)) return;
      reachable.add(neighbor);
      queue.push({ airportId: neighbor, hops: hops + 1 });
    });
  }

  return reachable;
}

/**
 * Client-side reach check (UX pre-validation): at least ONE endpoint must be within
 * two legs of an HQ or Base after the proposed service is added. A route may
 * extend from an endpoint within one existing leg, or join two already reachable
 * endpoints. The server enforces the same rule authoritatively.
 */
export function validateSegmentNetworkReach(
  originAirportId: number,
  destAirportId: number,
  teamId: number
): { isValid: boolean; errorKey?: string } {
  const airports = useAirportsStore.getState().airports;
  const segments = useSegmentsStore.getState().store.segments;

  const roots = getHQRootAirportIds(airports, teamId);
  if (roots.size === 0) {
    return { isValid: false, errorKey: 'tooFarFromNetworkRoot' };
  }

  const adjacency = buildTeamAirportAdjacency(teamId, segments);
  const oneLeg = computeReachableAirportsFromHQ(roots, adjacency, Airline4XRules.network_hops - 1);
  const twoLegs = computeReachableAirportsFromHQ(roots, adjacency, Airline4XRules.network_hops);
  if (oneLeg.has(originAirportId) || oneLeg.has(destAirportId) || (twoLegs.has(originAirportId) && twoLegs.has(destAirportId))) {
    return { isValid: true };
  }

  return { isValid: false, errorKey: 'tooFarFromNetworkRoot' };
}

/**
 * Valid connection origins for a first city investment; same roots and one-leg
 * boundary as route validation, from the headquarters, airports and routes
 * given — the globe's marks pass the ones they render from.
 */
export function getInvestmentConnectionOrigins(
  teamId: number,
  hqId: number | null,
  airports: Record<number, Airport>,
  segments: Record<number, Segment>,
): Set<number> {
  const roots = networkRoots(airports, teamId, hqId);
  return computeReachableAirportsFromHQ(roots, buildTeamAirportAdjacency(teamId, segments), Airline4XRules.network_hops - 1);
}

// ==================== STORE ====================

interface SegmentsState {
  /** The segment store data (mirrors server structure) */
  store: SegmentStoreData;
  /** Map of "team-origin-dest" -> segment IDs for route-level aggregation. */
  byTeamRoute: Record<string, number[]>;

  /** Segment IDs with pending modifications (stale load factor data until next tick) */
  pendingModifications: Set<number>;

  /**
   * A change to one of your flights on its way to the server — its flights a
   * day, or its removal — until the server answers the viewer (every player
   * hears every answer; only the viewer's own ends it) or the command gate
   * drops it.
   */
  flightChange: FlightChange | null;

  /** The flight the last removal took off: its page goes back to the list. */
  lastRemovedFlightId: number | null;

  /**
   * A new flight the route builder sent, from the press until the builder
   * has read the answer: on its way, refused and why, or added.
   */
  flightAdd: FlightAdd | null;

  /** Cached result from server for max flights per day and flight time query */
  maxFlightsQuery: {
    originId: number;
    destId: number;
    fleetSlotIndex: number;
    maxFlightsPerDay: number;
    flightTime: number;
  } | null;

  // Setters (update both segments and byCityPair index)
  setStore: (store: SegmentStoreData) => void;
  addSegment: (segment: Segment) => void;
  updateSegment: (segmentId: number, segment: Segment) => void;
  updateSegmentsBulk: (segments: Segment[], options?: { clearPendingIds?: number[] }) => void;
  removeSegment: (segmentId: number) => void;

  // Getters (O(1) lookups using index)
  getSegment: (segmentId: number) => Segment | undefined;

  // Pending modification tracking (for stale load factor display)
  markSegmentPending: (segmentId: number) => void;
  /** Take marks back that a change never sent, or that the server refused, had set. */
  unmarkSegmentsPending: (segmentIds: readonly number[]) => void;

  // Max flights query
  setMaxFlightsQuery: (query: SegmentsState['maxFlightsQuery']) => void;

  reset: () => void;
}

const initialState = {
  store: {
    segments: {},
    byCityPair: {},
  },
  byTeamRoute: {},
  pendingModifications: new Set<number>(),
  flightChange: null as FlightChange | null,
  lastRemovedFlightId: null as number | null,
  flightAdd: null as FlightAdd | null,
  maxFlightsQuery: null,
};

export const useSegmentsStore = create<SegmentsState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      /**
       * Set the complete store (on initial load from server)
       */
      setStore: (store) => {
        set(
          {
            store,
            byTeamRoute: buildByTeamRouteIndex(store.segments),
          },
          false,
          'setStore'
        );
      },

      /**
       * Add a segment (updates both segments map and byCityPair index)
       */
      addSegment: (segment) => {
        console.log('[SegmentsStore] Adding segment:', segment.segment_id);
        set(
          (state) => {
            const newSegments = {
              ...state.store.segments,
              [segment.segment_id]: segment,
            };
            
            // Update byCityPair index. A flight that joins a service the team
            // already flies comes back as that service's segment, which the
            // indexes already list: listed twice, the pair read it twice.
            const key = makeCityPairKey(segment.airport_id_origin, segment.airport_id_dest);
            const existingIds = state.store.byCityPair[key] || [];
            const newByCityPair = {
              ...state.store.byCityPair,
              [key]: existingIds.includes(segment.segment_id) ? existingIds : [...existingIds, segment.segment_id],
            };

            const routeKey = makeTeamRouteKey(segment.team_id, segment.airport_id_origin, segment.airport_id_dest);
            const existingRouteSegmentIds = state.byTeamRoute[routeKey] || [];
            const nextByTeamRoute = {
              ...state.byTeamRoute,
              [routeKey]: existingRouteSegmentIds.includes(segment.segment_id)
                ? existingRouteSegmentIds
                : [...existingRouteSegmentIds, segment.segment_id],
            };
            
            return {
              store: {
                segments: newSegments,
                byCityPair: newByCityPair,
              },
              byTeamRoute: nextByTeamRoute,
            };
          },
          false,
          'addSegment'
        );
      },

      /**
       * Update an existing segment
       */
      updateSegment: (segmentId, segment) => {
        set(
          (state) => {
            const previousSegment = state.store.segments[segmentId];
            const previousRouteKey = previousSegment
              ? makeTeamRouteKey(
                  previousSegment.team_id,
                  previousSegment.airport_id_origin,
                  previousSegment.airport_id_dest
                )
              : null;
            const nextRouteKey = makeTeamRouteKey(
              segment.team_id,
              segment.airport_id_origin,
              segment.airport_id_dest
            );

            let nextByTeamRoute = state.byTeamRoute;
            if (previousRouteKey !== nextRouteKey) {
              nextByTeamRoute = { ...state.byTeamRoute };

              if (previousRouteKey) {
                const previousRouteIds = nextByTeamRoute[previousRouteKey] || [];
                const remainingIds = previousRouteIds.filter((id) => id !== segmentId);
                if (remainingIds.length === 0) {
                  delete nextByTeamRoute[previousRouteKey];
                } else {
                  nextByTeamRoute[previousRouteKey] = remainingIds;
                }
              }

              const nextRouteIds = nextByTeamRoute[nextRouteKey] || [];
              if (!nextRouteIds.includes(segmentId)) {
                nextByTeamRoute[nextRouteKey] = [...nextRouteIds, segmentId];
              }
            }

            const nextSegments = {
              ...state.store.segments,
              [segmentId]: segment,
            };

            return {
              store: {
                ...state.store,
                segments: nextSegments,
              },
              ...(nextByTeamRoute !== state.byTeamRoute ? { byTeamRoute: nextByTeamRoute } : {}),
            };
          },
          false,
          'updateSegment'
        );
      },

      /**
       * Update multiple segments in a single store write.
       * Optionally clears pending state for selected segment IDs.
       */
      updateSegmentsBulk: (segments, options) => {
        if (segments.length === 0 && (!options?.clearPendingIds || options.clearPendingIds.length === 0)) {
          return;
        }

        set(
          (state) => {
            const nextSegments = { ...state.store.segments };
            for (const segment of segments) {
              nextSegments[segment.segment_id] = segment;
            }

            let nextPendingModifications = state.pendingModifications;
            if (options?.clearPendingIds && options.clearPendingIds.length > 0) {
              nextPendingModifications = new Set(state.pendingModifications);
              for (const segmentId of options.clearPendingIds) {
                nextPendingModifications.delete(segmentId);
              }
            }

            if (nextPendingModifications !== state.pendingModifications) {
              return {
                store: {
                  ...state.store,
                  segments: nextSegments,
                },
                byTeamRoute: buildByTeamRouteIndex(nextSegments),
                pendingModifications: nextPendingModifications,
              };
            }

            return {
              store: {
                ...state.store,
                segments: nextSegments,
              },
              byTeamRoute: buildByTeamRouteIndex(nextSegments),
            };
          },
          false,
          'updateSegmentsBulk'
        );
      },

      /**
       * Remove a segment (updates both segments map and byCityPair index)
       */
      removeSegment: (segmentId) => {
        set(
          (state) => {
            const segment = state.store.segments[segmentId];
            if (!segment) return state;
            
            // Remove from segments map
            const remainingSegments = { ...state.store.segments };
            delete remainingSegments[segmentId];
            
            // Update byCityPair index
            const key = makeCityPairKey(segment.airport_id_origin, segment.airport_id_dest);
            const existingIds = state.store.byCityPair[key] || [];
            const newIds = existingIds.filter(id => id !== segmentId);
            
            const newByCityPair = { ...state.store.byCityPair };
            if (newIds.length === 0) {
              delete newByCityPair[key];
            } else {
              newByCityPair[key] = newIds;
            }

            const routeKey = makeTeamRouteKey(segment.team_id, segment.airport_id_origin, segment.airport_id_dest);
            const routeSegmentIds = state.byTeamRoute[routeKey] || [];
            const remainingRouteSegmentIds = routeSegmentIds.filter((id) => id !== segmentId);
            const nextByTeamRoute = { ...state.byTeamRoute };
            if (remainingRouteSegmentIds.length === 0) {
              delete nextByTeamRoute[routeKey];
            } else {
              nextByTeamRoute[routeKey] = remainingRouteSegmentIds;
            }
            
            return {
              store: {
                segments: remainingSegments,
                byCityPair: newByCityPair,
              },
              byTeamRoute: nextByTeamRoute,
            };
          },
          false,
          'removeSegment'
        );
      },

      getSegment: (segmentId) => get().store.segments[segmentId],

      /**
       * Mark a segment as having pending modifications (stale load factor)
       */
      markSegmentPending: (segmentId) => {
        set(
          (state) => {
            const newSet = new Set(state.pendingModifications);
            newSet.add(segmentId);
            return { pendingModifications: newSet };
          },
          false,
          'markSegmentPending'
        );
      },

      unmarkSegmentsPending: (segmentIds) => {
        set(
          (state) => {
            if (!segmentIds.some((id) => state.pendingModifications.has(id))) return state;
            const next = new Set(state.pendingModifications);
            for (const id of segmentIds) next.delete(id);
            return { pendingModifications: next };
          },
          false,
          'unmarkSegmentsPending'
        );
      },

      setMaxFlightsQuery: (query) => {
        set({ maxFlightsQuery: query }, false, 'setMaxFlightsQuery');
      },

      /**
       * Reset the segments store to initial state
       */
      reset: () => {
        set(
          {
            ...initialState,
            pendingModifications: new Set<number>(),
          },
          false,
          'reset'
        );
      },
    }),
    {
      name: 'segments-store',
    }
  )
);

/** A change to one of your flights on its way: its flights a day, or its removal. */
interface FlightChange {
  kind: 'frequency' | 'remove';
  segmentId: number;
  flightId: number;
  /** The flights a day asked for, when that is the change. */
  flightsPerDay?: number;
}

/** The change on its way is over — answered, refused or dropped — if it is still `kind`. */
function endFlightChange(kind: FlightChange['kind']): FlightChange | null {
  const change = useSegmentsStore.getState().flightChange;
  if (change?.kind !== kind) return null;
  useSegmentsStore.setState({ flightChange: null }, false, 'endFlightChange');
  return change;
}

/**
 * A success every player hears answers the viewer's change only when it is the
 * viewer's own, about that flight: a rival's or a teammate's answer landing
 * first leaves the wait as it was.
 */
function answersOwnChange(sender: string | undefined, matches: (change: FlightChange) => boolean): boolean {
  const change = useSegmentsStore.getState().flightChange;
  return change !== null && sender !== undefined && sender === getViewerUserId() && matches(change);
}

/** A new flight, as the route builder asks for it: the route, the aircraft, how often. */
export interface FlightAddRequest {
  originId: number;
  destId: number;
  fleetSlotIndex: number;
  flightsPerDay: number;
}

/**
 * A new flight the route builder sent, and where it stands: on its way;
 * refused, with the server's refusal as it came — its code, its figures and
 * its English; never sent, with the gate's reason; or added, as this segment —
 * a new one, or the service it joined. A refusal and a reason are worded as
 * the builder draws them (flightAddFailureText), so they read in the
 * player's language of the moment. `unattended` once the builder that sent it
 * has closed: a refusal then toasts, with nothing left to show it.
 */
type FlightAdd = FlightAddRequest & { unattended?: boolean } & (
  | { state: 'sent' }
  | { state: 'refused'; refusal: SegmentRefusal }
  | { state: 'notSent'; reason: NotSentReason }
  | { state: 'added'; segmentId: number }
);

/** What was asked for, without where it stands. */
function requestOf({ originId, destId, fleetSlotIndex, flightsPerDay }: FlightAddRequest): FlightAddRequest {
  return { originId, destId, fleetSlotIndex, flightsPerDay };
}

/**
 * The route builder is closing — as the close is asked for, not once it has
 * slid out, so an answer landing meanwhile is not shown to a panel on its way
 * out. An answer it has read is done with; one still on its way will find no
 * builder to show it, so a refusal toasts instead.
 */
export function leaveFlightAdd(): void {
  const add = useSegmentsStore.getState().flightAdd;
  if (!add) return;
  useSegmentsStore.setState({ flightAdd: add.state === 'sent' ? { ...add, unattended: true } : null }, false, 'leaveFlightAdd');
}

/**
 * A flight still on its way for this route — either way round — from a
 * builder closed since, as it was asked: its aircraft and how often. A
 * builder opening on the route takes it back (attendFlightAdd) and shows it
 * as it was sent, so its refusal is said at that builder's foot and, added,
 * the dock turns to the route's flights.
 */
export function unattendedFlightAdd(originId: number, destId: number): FlightAddRequest | null {
  const add = useSegmentsStore.getState().flightAdd;
  if (add?.state !== 'sent' || !add.unattended) return null;
  return makeCityPairKey(add.originId, add.destId) === makeCityPairKey(originId, destId) ? requestOf(add) : null;
}

/** The builder open on this route answers for the flight on its way for it again. */
export function attendFlightAdd(originId: number, destId: number): void {
  const add = useSegmentsStore.getState().flightAdd;
  if (!add || unattendedFlightAdd(originId, destId) === null) return;
  useSegmentsStore.setState({ flightAdd: { ...add, unattended: false } }, false, 'attendFlightAdd');
}

// ==================== REFUSALS ====================

/**
 * A new flight, or a change to one of your flights, that the server refused,
 * as it arrives: an OrderRefusal, and where its code carries them, the ends of
 * the route it names (`airports`, by airport id) and the most flights a day
 * the route allows (`most`).
 */
export type SegmentRefusal = OrderRefusal & { airports?: number[]; most?: number };

/**
 * The reasons the server refuses a new flight, or a change to one of your
 * flights, for, where a player can meet them — segments.go sends each as a
 * code beside the English — by the key that says each in the player's
 * language. A Go test reads this table (segment_refusal_test.go), so the two
 * sides name the same codes.
 *
 * Each is said in the sentence new-flight-words.ts builds for the surface that
 * says the same reason before the press, so a refusal reads as it does, and
 * its keys here are those sentences' own, listed so the codes are named. A new
 * flight's are the route builder's reasons (new-flight.ts): the route's — the
 * airline not in a city, outside the network, no flight slot free at an end
 * (or at either: `no_slot` naming both) — as the resting Add flight's card
 * says them, then the sub-fleet's — short of the far end, an end too small for
 * it, all of it flying — as the card says a chosen one rests. A sub-fleet a
 * teammate has removed is said as Fleet says it. More flights a day than the
 * route allows, a new flight's or a flight's own, is said as a flight's page
 * says its most; a change to a flight a teammate has just taken off its route,
 * as that page says a flight gone.
 */
const SEGMENT_REFUSALS = {
  uninvested: 'globe.drag.investToFly',
  outside_network: 'globe.builder.outsideNetworkNote',
  no_slot: 'globe.builder.noSlotAtNote',
  not_set_up: 'fleet.refusal.notSetUp',
  out_of_range: 'globe.builder.restRange',
  too_small: 'globe.builder.restAirport',
  flying: 'globe.builder.restFlying',
  most_a_day: 'globe.flight.mostADay',
  flight_gone: 'globe.flight.goneNote',
} as const;

/** What a refused change was about: a route, either way round as it was asked, and the sub-fleet flying it. */
interface RefusedRoute {
  originId: number;
  destId: number;
  fleetSlotIndex: number;
}

/** The route a segment flies, and its sub-fleet, as a refusal of a change to one of its flights reads it. */
function routeOfSegment(segment: Segment | undefined): RefusedRoute | null {
  return segment ? { originId: segment.airport_id_origin, destId: segment.airport_id_dest, fleetSlotIndex: segment.fleet_slot_index } : null;
}

/**
 * What a refused new flight, or change to one of your flights, tells the
 * player, about the route it asked for (null when that is not known): the
 * sentence the same reason is said in before the press, from the server's
 * figures — its cities named in the order the builder holds the route, its
 * sub-fleet counted and named as the fleet holds it. Null when the server sent
 * no reason the client knows — a failure no player meets in play, or a code
 * from a newer server — or a reason that lost what it names, and the server's
 * words stand.
 */
export function readSegmentRefusal({ reason, need, have, most, airports }: SegmentRefusal, route: RefusedRoute | null): OrderBlock | null {
  const key = refusalKey(SEGMENT_REFUSALS, reason);
  if (key === undefined) return null;
  // The ends the refusal names, as the builder holds the route: its origin first.
  const named = Array.isArray(airports) ? airports : [];
  const ends = route ? [route.originId, route.destId].filter((id) => named.includes(id)) : named;
  if (reason === 'uninvested') {
    // The builder's origin is always the airline's, so it names one city.
    return ends.length > 0 ? investToFly(ends[0]) : null;
  }
  if (reason === 'outside_network') return outsideNetworkNote();
  if (reason === 'no_slot') return ends.length > 0 ? noSlotNote(ends) : null;
  if (reason === 'out_of_range') {
    if (!route || typeof need !== 'number' || typeof have !== 'number') return null;
    return rangeShort(kmShort(need, have), route.destId);
  }
  if (reason === 'too_small') {
    if (ends.length === 0 || typeof need !== 'number' || typeof have !== 'number' || need < 1) return null;
    // The server names the smaller end, and both when they are the same size:
    // the builder's rule then picks the destination.
    const end = route ? smallerEndOf(route.originId, route.destId, (id) => (ends.includes(id) ? have : Infinity)) : ends[0];
    return airportTooSmall(need, end, have);
  }
  if (reason === 'flying') {
    // Counted as the builder counts the sub-fleet on its card.
    const total = route ? getFleetSlot(getCurrentFleet(), route.fleetSlotIndex)?.total : undefined;
    return total ? allFlying(total) : null;
  }
  if (reason === 'most_a_day') {
    if (!route || typeof most !== 'number') return null;
    const aircraft = aircraftOf(getCurrentFleet(), route.fleetSlotIndex);
    return mostADay(most, routeCodes(route.originId, route.destId), aircraft?.name ?? null);
  }
  return { key };
}

/**
 * What a new flight the builder could not add says, worded now, so it follows
 * the player's language: a refusal's reason when the client knows its code,
 * else the server's words; a flight that never went, the gate's reason.
 */
export function flightAddFailureText(add: Extract<FlightAdd, { state: 'refused' | 'notSent' }>): string {
  return add.state === 'notSent'
    ? i18n.t(NOT_SENT_WORDS[add.reason])
    : refusalText(readSegmentRefusal(add.refusal, add), add.refusal);
}

// ==================== HANDLERS ====================

/**
 * Handle OpCodeSegmentsState message
 * Sets the complete segments state for the match (on join)
 */
export function handleSegmentsState(data: {
  segments: Record<number, Segment>;
  by_city_pair: Record<number, number[]>;
}) {
  console.log('[SegmentsHandler] Handling segments state:', Object.keys(data.segments || {}).length, 'segments');
  
  const store = useSegmentsStore.getState();
  store.setStore({
    segments: data.segments || {},
    byCityPair: data.by_city_pair || {},
  });
}

/**
 * Handle OpCodeAddSegmentSuccess message
 * Adds the new segment and updates the fleet and airport-capacity stores.
 */
export function handleAddSegmentSuccess(data: {
  segment: Segment;
  fleet: Fleet;
  cohort_id: string;
  origin_airport?: Airport;
  dest_airport?: Airport;
  /** Who pressed Add flight: the server tells every client. */
  sender_user_id?: string;
}) {
  // DEBUG: Enhanced logging for flight instance testing
  console.log('[FLIGHT_DEBUG] handleAddSegmentSuccess', {
    segment_id: data.segment.segment_id,
    route: `${data.segment.airport_id_origin}↔${data.segment.airport_id_dest}`,
    plane_count: data.segment.plane_count,
    max_flights_per_day: data.segment.max_flights_per_day,
    current_flights_per_day: data.segment.current_flights_per_day,
    flights: data.segment.flights?.map(f => ({
      id: f.flight_id,
      number: f.flight_number,
      freq: f.flights_per_day,
    })),
    seats_per_day: {
      econ: data.segment.seats_per_day[0],
      business: data.segment.seats_per_day[1],
      first: data.segment.seats_per_day[2],
    },
    itinerary_count: data.segment.itinerary_count,
  });
  console.log('[SegmentsHandler] Segment added:', data.segment.segment_id);

  const store = useSegmentsStore.getState();
  
  // Add segment to store
  store.addSegment(data.segment);
  
  // Mark new segment as pending (load factor hasn't been calculated yet)
  store.markSegmentPending(data.segment.segment_id);
  
  // Update fleet store (planes were assigned, Unassigned decreased)
  handleFleetUpdate(data.cohort_id, data.fleet);
  
  // Update airports after capacity was assigned.
  if (data.origin_airport) handleAirportUpdate(data.origin_airport);
  if (data.dest_airport) handleAirportUpdate(data.dest_airport);

  // The viewer's own flight, answered: the builder that sent it opens the
  // route's flights on it — last, so the segment is in the store by then.
  const add = useSegmentsStore.getState().flightAdd;
  if (data.sender_user_id !== undefined && data.sender_user_id === getViewerUserId() && add?.state === 'sent') {
    useSegmentsStore.setState(
      { flightAdd: add.unattended ? null : { ...requestOf(add), state: 'added', segmentId: data.segment.segment_id } },
      false,
      'flightAdded',
    );
  }
}

/**
 * Handle OpCodeUpdateFlightFrequencySuccess message
 * Updates the segment in the store (flight frequency changed), marks segment as pending
 */
export function handleUpdateFlightFrequencySuccess(data: {
  segment: Segment;
  cohort_id: string;
  /** Who changed it: every player hears it. */
  sender_user_id?: string;
}) {
  // DEBUG: Enhanced logging for flight frequency testing
  console.log('[FLIGHT_DEBUG] handleUpdateFlightFrequencySuccess', {
    segment_id: data.segment.segment_id,
    plane_count: data.segment.plane_count,
    current_flights_per_day: data.segment.current_flights_per_day,
    max_flights_per_day: data.segment.max_flights_per_day,
    flights: data.segment.flights?.map(f => ({
      id: f.flight_id,
      number: f.flight_number,
      freq: f.flights_per_day,
    })),
    seats_per_day: {
      econ: data.segment.seats_per_day[0],
      business: data.segment.seats_per_day[1],
      first: data.segment.seats_per_day[2],
    },
  });
  console.log('[SegmentsHandler] Flight frequency updated:', data.segment.segment_id);

  handleSegmentUpdate(data.segment, { stale: true });
  if (answersOwnChange(data.sender_user_id, (change) => change.kind === 'frequency' && change.segmentId === data.segment.segment_id)) {
    endFlightChange('frequency');
  }
}

/**
 * Handle OpCodeRemoveFlightSuccess message
 * Updates or removes the segment, updates fleet and airports
 */
export function handleRemoveFlightSuccess(data: {
  segment_id: number;
  flight_id: number;
  fleet: Fleet;
  cohort_id: string;
  segment_removed?: boolean;
  segment?: Segment;
  origin_airport?: Airport;
  dest_airport?: Airport;
  /** Who removed it: every player hears it. */
  sender_user_id?: string;
}) {
  // DEBUG: Enhanced logging for flight removal testing
  console.log('[FLIGHT_DEBUG] handleRemoveFlightSuccess', {
    segment_id: data.segment_id,
    flight_id_removed: data.flight_id,
    segment_removed: data.segment_removed,
    remaining_segment: data.segment
      ? {
          plane_count: data.segment.plane_count,
          flights: data.segment.flights?.map(f => ({
            id: f.flight_id,
            number: f.flight_number,
            freq: f.flights_per_day,
          })),
          seats_per_day: {
            econ: data.segment.seats_per_day[0],
            business: data.segment.seats_per_day[1],
            first: data.segment.seats_per_day[2],
          },
        }
      : 'segment_was_removed',
  });
  console.log('[SegmentsHandler] Flight removed:', data.segment_id, data.flight_id);

  const store = useSegmentsStore.getState();
  if (data.segment_removed) {
    console.log('[FLIGHT_DEBUG] Segment fully removed (last flight was removed):', data.segment_id);
    store.removeSegment(data.segment_id);
  } else if (data.segment) {
    handleSegmentUpdate(data.segment, { stale: true });
  }

  handleFleetUpdate(data.cohort_id, data.fleet);

  if (data.origin_airport) handleAirportUpdate(data.origin_airport);
  if (data.dest_airport) handleAirportUpdate(data.dest_airport);
  // Whoever removed it, the flight is gone: its page goes back to the list.
  useSegmentsStore.setState({ lastRemovedFlightId: data.flight_id }, false, 'flightRemoved');
  if (answersOwnChange(data.sender_user_id, (change) => change.kind === 'remove' && change.flightId === data.flight_id)) {
    endFlightChange('remove');
  }
}

/** What was refused: a new flight, a flight's flights a day, or a flight's removal. */
type SegmentOperation = 'Add segment' | 'Update flight frequency' | 'Remove flight';

/**
 * A new flight that never reached the server: the builder that sent it keeps
 * the gate's reason, to say at its foot as a refusal is said; with no builder
 * left to say it, a toast does, in the gate's words.
 */
function handleFlightAddNotSent(refusal: { error: string }, reason: NotSentReason): void {
  const add = useSegmentsStore.getState().flightAdd;
  if (add?.state === 'sent' && !add.unattended) {
    useSegmentsStore.setState({ flightAdd: { ...requestOf(add), state: 'notSent', reason } }, false, 'flightAddNotSent');
    return;
  }
  useSegmentsStore.setState({ flightAdd: null }, false, 'flightAddNotSent');
  showErrorToast(i18n.t('globe.builder.addFailed'), refusal.error);
}

/**
 * A refused change to a route or a flight: a new flight's at the foot of the
 * route builder that sent it; any other, or a new flight whose builder has
 * closed, in one toast titled for what was refused — the flight's page shows
 * none of its own. Each says why in the player's language when the server
 * sent a reason the client knows (readSegmentRefusal), else in the server's
 * words. A change on its way is over.
 */
export function handleSegmentOperationFailed(data: SegmentRefusal, operation: SegmentOperation) {
  // DEBUG: Enhanced logging for failure testing
  console.error('[FLIGHT_DEBUG] OPERATION FAILED', {
    operation,
    error: data.error,
    reason: data.reason,
    timestamp: new Date().toISOString(),
  });
  console.error(`[SegmentsHandler] ❌ ${operation} failed:`, data.reason ?? '', data.error);

  // A new flight: the builder that sent it keeps the refusal, to say at its
  // foot as it draws it. With no builder left to say it, a toast does, once.
  if (operation === 'Add segment') {
    const add = useSegmentsStore.getState().flightAdd;
    // The route allows fewer flights a day than the builder last heard — a
    // modifier changed since it asked — so it asks again, and offers what the
    // server will take (use-flight-frequency.ts reads the answer).
    if (add && data.reason === 'most_a_day') void queryMaxFlights(add.originId, add.destId, add.fleetSlotIndex);
    if (add?.state === 'sent' && !add.unattended) {
      useSegmentsStore.setState({ flightAdd: { ...requestOf(add), state: 'refused', refusal: data } }, false, 'flightAddRefused');
      return;
    }
    useSegmentsStore.setState({ flightAdd: null }, false, 'flightAddRefused');
    showErrorToast(i18n.t('globe.builder.addFailed'), refusalText(readSegmentRefusal(data, add), data));
    return;
  }
  // Show error to user (non-blocking toast — never alert(), which freezes the game)
  if (operation === 'Update flight frequency') {
    const change = endFlightChange('frequency');
    const segment = change ? useSegmentsStore.getState().store.segments[change.segmentId] : undefined;
    showErrorToast(i18n.t('globe.flight.frequencyFailed'), refusalText(readSegmentRefusal(data, routeOfSegment(segment)), data));
    return;
  }
  if (operation === 'Remove flight') {
    const change = endFlightChange('remove');
    const segment = change ? useSegmentsStore.getState().store.segments[change.segmentId] : undefined;
    const number = segment?.flights?.find((flight) => flight.flight_id === change?.flightId)?.flight_number;
    showErrorToast(
      number ? i18n.t('globe.flight.removeFailed', { number }) : i18n.t('globe.flight.removeFailedAny'),
      refusalText(readSegmentRefusal(data, routeOfSegment(segment)), data),
    );
  }
}

/**
 * Handler for team_segments_state message
 * Sent when player joins the match with all their team's segments
 */
export function handleTeamSegmentsState(data: {
  team_id: number;
  segments: Segment[];
}) {
  const store = useSegmentsStore.getState();

  // Update all team segments in one store write.
  store.updateSegmentsBulk(data.segments);
}

/**
 * Handler for team_segments_update message
 * Sent after manifest processing with updated load factors for all team segments
 */
export function handleTeamSegmentsUpdate(data: {
  team_id: number;
  segments: Segment[];
  partial?: boolean;
}) {
  const store = useSegmentsStore.getState();

  // Update all team segments with fresh data from server.
  if (data.partial) {
    store.updateSegmentsBulk(data.segments);
    return;
  }

  store.updateSegmentsBulk(data.segments, {
    clearPendingIds: data.segments.map((segment) => segment.segment_id),
  });
}

/**
 * A segment as another domain's message carries it: a sub-fleet's new seats
 * (fleets.ts), a plane innovation's figures (innovation.ts). One whose seats
 * changed is `stale`: its load factor waits for the next tick to say it.
 */
export function handleSegmentUpdate(segment: Segment, { stale = false }: { stale?: boolean } = {}): void {
  const store = useSegmentsStore.getState();
  store.updateSegment(segment.segment_id, segment);
  if (stale) store.markSegmentPending(segment.segment_id);
}

/**
 * A price change on its way (itineraries.ts) holds its itinerary's segments:
 * their load factors wait for the next update to say the new price's effect.
 */
export function handlePriceChangePending(segmentIds: readonly number[]): void {
  const store = useSegmentsStore.getState();
  for (const id of segmentIds) store.markSegmentPending(id);
}

/** A price change refused or never sent changed nothing: its segments wait no longer. */
export function handlePriceChangeUndone(segmentIds: readonly number[]): void {
  useSegmentsStore.getState().unmarkSegmentsPending(segmentIds);
}

// ==================== SERVICE METHODS ====================

/**
 * Add a new segment (creates flight route between two airports), or a flight
 * to the service the team already flies there in that sub-fleet. Where it
 * stands is `flightAdd`, until the builder has read the answer.
 * Server will extract cohort_id from player data (server-authoritative)
 * @param originAirportId - Origin airport ID
 * @param destAirportId - Destination airport ID
 * @param fleetSlotIndex - Index into the team's flat fleet slots array
 * @param flightsPerDay - Flights per day for the new flight instance
 * @param planeCount - Number of planes to assign to this segment (defaults to 1)
 */
export async function addSegment(
  originAirportId: number,
  destAirportId: number,
  fleetSlotIndex: number,
  flightsPerDay: number,
  planeCount = 1
): Promise<void> {
  // Order airport IDs (client should send them in order, but we ensure it here)
  const [orderedOrigin, orderedDest] = orderAirportIds(originAirportId, destAirportId);

  const message = {
    type: MessageType.MessageTypeAddSegment,
    origin_airport_id: orderedOrigin,
    dest_airport_id: orderedDest,
    fleet_slot_index: fleetSlotIndex,
    plane_count: planeCount,
    flights_per_day: flightsPerDay,
  };

  console.log('[SegmentsService] Adding segment:', orderedOrigin, '->', orderedDest, 
    'slot:', fleetSlotIndex, 'planes:', planeCount, 'flights/day:', flightsPerDay);

  useSegmentsStore.setState(
    { flightAdd: { originId: originAirportId, destId: destAirportId, fleetSlotIndex, flightsPerDay, state: 'sent' } },
    false,
    'sendFlightAdd',
  );
  await sendGatedCommand(OpCode.OpCodeAddSegment, JSON.stringify(message), {
    // Nothing will answer, and the builder's Add flight comes back — unless
    // the flight never went, which the builder says.
    endWait: () => {
      if (useSegmentsStore.getState().flightAdd?.state === 'sent') {
        useSegmentsStore.setState({ flightAdd: null }, false, 'endFlightAdd');
      }
    },
    notSent: handleFlightAddNotSent,
  });
}

/**
 * Update flights per day for a specific flight instance.
 * Server will extract cohort_id from player data (server-authoritative)
 * @param segmentId - The segment ID to update
 * @param flightId - The flight instance ID
 * @param flightsPerDay - New flights per day value
 */
export async function updateFlightFrequency(
  segmentId: number,
  flightId: number,
  flightsPerDay: number
): Promise<void> {
  const message = {
    type: MessageType.MessageTypeUpdateFlightFrequency,
    segment_id: segmentId,
    flight_id: flightId,
    flights_per_day: flightsPerDay,
  };

  console.log('[SegmentsService] Updating flight frequency:', segmentId, flightId, flightsPerDay);

  useSegmentsStore.setState({ flightChange: { kind: 'frequency', segmentId, flightId, flightsPerDay } }, false, 'sendFlightFrequency');
  await sendGatedCommand(OpCode.OpCodeUpdateFlightFrequency, JSON.stringify(message), {
    endWait: () => endFlightChange('frequency'),
    notSent: (refusal) => handleSegmentOperationFailed(refusal, 'Update flight frequency'),
  });
}

/**
 * Remove a specific flight instance from a segment.
 * Server will extract cohort_id from player data (server-authoritative)
 * @param segmentId - The segment ID to update
 * @param flightId - The flight instance ID to remove
 */
export async function removeFlight(
  segmentId: number,
  flightId: number
): Promise<void> {
  const message = {
    type: MessageType.MessageTypeRemoveFlight,
    segment_id: segmentId,
    flight_id: flightId,
  };

  console.log('[SegmentsService] Removing flight:', segmentId, flightId);

  useSegmentsStore.setState({ flightChange: { kind: 'remove', segmentId, flightId } }, false, 'sendRemoveFlight');
  await sendGatedCommand(OpCode.OpCodeRemoveFlight, JSON.stringify(message), {
    endWait: () => endFlightChange('remove'),
    notSent: (refusal) => handleSegmentOperationFailed(refusal, 'Remove flight'),
  });
}

// ===== MAX FLIGHTS QUERY =====

/**
 * Query the server for the max flights per day for a given route and plane.
 * The server applies all modifiers (turnaround, flight time) and returns the result.
 * A question that cannot be asked says nothing to the player: the route
 * builder estimates both with the server's own formulas until an answer comes
 * (use-flight-frequency.ts), and the server checks the flight it is sent.
 */
export async function queryMaxFlights(
  originAirportId: number,
  destAirportId: number,
  fleetSlotIndex: number
): Promise<void> {
  const message = {
    type: MessageType.MessageTypeQueryMaxFlights,
    origin_airport_id: originAirportId,
    dest_airport_id: destAirportId,
    fleet_slot_index: fleetSlotIndex,
  };

  await sendToMatch(OpCode.OpCodeQueryMaxFlights, message, 'a max-flights query');
}

/**
 * Handle the server's response with the calculated max flights per day.
 * Echoed params allow the client to discard stale responses.
 */
export function handleQueryMaxFlightsResult(data: {
  max_flights_per_day: number;
  flight_time: number;
  origin_airport_id: number;
  dest_airport_id: number;
  fleet_slot_index: number;
}) {
  useSegmentsStore.getState().setMaxFlightsQuery({
    originId: data.origin_airport_id,
    destId: data.dest_airport_id,
    fleetSlotIndex: data.fleet_slot_index,
    maxFlightsPerDay: data.max_flights_per_day,
    flightTime: data.flight_time,
  });
}
